import React, { useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../UI/card';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { useLocale } from '../../contexts/LocaleContext';

const PROFILE_FIELDS = [
  { key: 'name', labelKey: 'tenantProfileName', required: true },
  { key: 'legal_name', labelKey: 'tenantProfileLegalName' },
  { key: 'duns_number', labelKey: 'tenantProfileDuns' },
  { key: 'country', labelKey: 'tenantProfileCountry' },
  { key: 'city', labelKey: 'tenantProfileCity' },
  { key: 'industry', labelKey: 'tenantProfileIndustry' },
  { key: 'annual_revenue', labelKey: 'tenantProfileAnnualRevenue' },
  { key: 'employee_count', labelKey: 'tenantProfileEmployeeCount', type: 'number' },
  { key: 'stock_ticker', labelKey: 'tenantProfileStockTicker' },
];

const normalizeProfileValue = (profile, key) => {
  const value = profile?.[key];
  return value == null ? '' : String(value);
};

function TenantProfileForm({ profile, saving, onSave, embedded = false }) {
  const { t } = useLocale();
  const [draft, setDraft] = useState(() => (profile ? { ...profile } : {}));

  React.useEffect(() => {
    setDraft(profile ? { ...profile } : {});
  }, [profile]);

  const hasName = useMemo(() => String(draft?.name || '').trim().length > 0, [draft]);

  const handleChange = (key, rawValue) => {
    setDraft((prev) => ({
      ...prev,
      [key]: rawValue,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = { ...draft };
    if (payload.employee_count === '') {
      delete payload.employee_count;
    } else if (payload.employee_count != null) {
      payload.employee_count = Number(payload.employee_count);
    }
    onSave(payload);
  };

  const formBody = (
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PROFILE_FIELDS.map((field) => (
              <div key={field.key} className="space-y-1">
                <label className="text-sm font-medium text-text-secondary">
                  {t(field.labelKey)}
                  {field.required ? ' *' : ''}
                </label>
                <Input
                  type={field.type || 'text'}
                  value={normalizeProfileValue(draft, field.key)}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  required={Boolean(field.required)}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving || !hasName}>
              <Save className="h-4 w-4" />
              {saving ? t('tenantSaveInProgress') : t('tenantSaveProfile')}
            </Button>
          </div>
        </form>
  );

  if (embedded) {
    return formBody;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('tenantProfileTitle')}</CardTitle>
        <CardDescription>{t('tenantProfileDescription')}</CardDescription>
      </CardHeader>
      <CardContent>{formBody}</CardContent>
    </Card>
  );
}

export default TenantProfileForm;

